import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, SearchLg, Eye, Trash01 } from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Dialog, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import JobForm from "./JobForm";
import {
  getJobs,
  createJob,
  deleteJob,
  type JobWithRelations,
  type CreateJobPayload,
} from "@/api/jobs";
import type { PaginatedResponse } from "@/types/dto";

const CAN_CREATE = ["admin", "owner", "manager", "receptionist"];
const CAN_DELETE = ["admin", "owner"];

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  awaiting_parts: "bg-yellow-100 text-yellow-700",
  completed: "bg-green-100 text-green-700",
  invoiced: "bg-purple-100 text-purple-700",
  cancelled: "bg-red-100 text-red-700",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  awaiting_parts: "Awaiting Parts",
  completed: "Completed",
  invoiced: "Invoiced",
  cancelled: "Cancelled",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-700"}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export default function JobsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [result, setResult] = useState<PaginatedResponse<JobWithRelations> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const canCreate = CAN_CREATE.includes(user?.role ?? "");
  const canDelete = CAN_DELETE.includes(user?.role ?? "");

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getJobs({ page, limit: 20, search: search || undefined });
      setResult(data);
    } catch {
      // handled
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleCreate = async (payload: CreateJobPayload) => {
    setSaving(true);
    try {
      await createJob(payload);
      setFormOpen(false);
      fetchJobs();
      toast.success("Job created");
    } catch {
      toast.error("Failed to create job");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    setSaving(true);
    try {
      await deleteJob(deleteConfirmId);
      setDeleteConfirmId(null);
      fetchJobs();
      toast.success("Job deleted");
    } catch {
      toast.error("Failed to delete job");
    } finally {
      setSaving(false);
    }
  };

  const jobs = result?.data ?? [];
  const pagination = result?.pagination;

  return (
    <PageLayout
      title="Jobs"
      subtitle="Track and manage repair jobs"
      actions={
        canCreate ? (
          <Button
            size="md"
            color="primary"
            iconLeading={Plus}
            onClick={() => setFormOpen(true)}
          >
            New Job
          </Button>
        ) : undefined
      }
    >
      <div className="mb-6 max-w-sm">
        <Input
          placeholder="Search by job number or description..."
          icon={SearchLg}
          value={search}
          onChange={(v: string) => { setSearch(v); setPage(1); }}
        />
      </div>

      {loading && jobs.length === 0 ? (
        <div className="py-12 text-center text-text-tertiary">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="py-12 text-center text-text-tertiary">
          {search ? "No jobs match your search." : "No jobs yet."}
        </div>
      ) : (
        <>
          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 xl:hidden">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="rounded-xl border border-border-secondary bg-bg-primary p-4"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-text-primary">Job #{job.jobNumber}</p>
                      <StatusBadge status={job.status} />
                    </div>
                    {job.description && (
                      <p className="text-sm text-text-secondary mt-0.5 truncate">{job.description}</p>
                    )}
                    <p className="text-xs text-text-tertiary mt-1">
                      {job.customer ? `${job.customer.firstName} ${job.customer.lastName}` : "No customer"}
                      {job.vehicle ? ` · ${job.vehicle.make} ${job.vehicle.model}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <Button size="sm" color="link-gray" iconLeading={Eye} onClick={() => navigate(`/jobs/${job.id}`)} aria-label="View" />
                    {canDelete && (
                      <Button size="sm" color="link-destructive" iconLeading={Trash01} onClick={() => setDeleteConfirmId(job.id)} aria-label="Delete" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="hidden xl:block overflow-x-auto rounded-xl border border-border-secondary">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-secondary bg-bg-secondary">
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Job #</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Description</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Customer</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Vehicle</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Status</th>
                  <th className="px-6 py-3 text-right font-medium text-text-tertiary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    className="border-b border-border-secondary last:border-0 hover:bg-bg-secondary_hover cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  >
                    <td className="px-6 py-4 font-medium text-text-primary">#{job.jobNumber}</td>
                    <td className="px-6 py-4 text-text-secondary max-w-xs truncate">{job.description ?? "—"}</td>
                    <td className="px-6 py-4 text-text-secondary">
                      {job.customer ? `${job.customer.firstName} ${job.customer.lastName}` : "—"}
                    </td>
                    <td className="px-6 py-4 text-text-secondary">
                      {job.vehicle ? `${job.vehicle.make} ${job.vehicle.model} (${job.vehicle.year})` : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" color="link-gray" iconLeading={Eye} onClick={() => navigate(`/jobs/${job.id}`)} aria-label="View" />
                        {canDelete && (
                          <Button size="sm" color="link-destructive" iconLeading={Trash01} onClick={() => setDeleteConfirmId(job.id)} aria-label="Delete" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-text-tertiary">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} jobs)
              </p>
              <div className="flex gap-2">
                <Button size="sm" color="secondary" isDisabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                <Button size="sm" color="secondary" isDisabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogTitle>New Job</DialogTitle>
        <DialogDescription>Create a new repair job.</DialogDescription>
        <JobForm
          onSubmit={handleCreate}
          onCancel={() => setFormOpen(false)}
          isLoading={saving}
        />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogTitle>Delete Job</DialogTitle>
        <DialogDescription>Are you sure you want to delete this job? This cannot be undone.</DialogDescription>
        <div className="flex justify-end gap-3">
          <Button size="md" color="secondary" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button size="md" color="primary-destructive" isLoading={saving} isDisabled={saving} onClick={handleDelete}>Delete</Button>
        </div>
      </Dialog>
    </PageLayout>
  );
}
